import ClientMonitor from 'skywalking-client-js';

type RouterState = {
  location: {
    pathname: string;
    search: string;
    hash: string;
  };
  navigation: {
    state: string;
  };
};

type RouterLike = {
  subscribe: (listener: (state: RouterState) => void) => () => void;
};

type BrowserMonitoringConfig = {
  collector?: string;
  enabled: boolean;
  service: string;
  serviceVersion: string;
};

type RuntimeConfig = NonNullable<Window['__POLARIS_RUNTIME_CONFIG__']>;

function getRuntimeConfig(): RuntimeConfig {
  return window.__POLARIS_RUNTIME_CONFIG__ ?? {};
}

function isEnabled(rawValue: string | undefined): boolean {
  return rawValue === 'true';
}

function currentPagePath(locationLike: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  const path = `${locationLike.pathname}${locationLike.search}${locationLike.hash}`;
  return path || '/';
}

function resolveCollectorUrl(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  return `${window.location.origin}${raw}`;
}

export function getBrowserMonitoringConfig(): BrowserMonitoringConfig {
  const runtimeConfig = getRuntimeConfig();
  const collector = resolveCollectorUrl(runtimeConfig.skywalkingBrowserCollector?.trim());

  return {
    collector,
    enabled: isEnabled(runtimeConfig.skywalkingBrowserEnabled),
    service: runtimeConfig.skywalkingBrowserService?.trim() || 'frontend',
    serviceVersion: runtimeConfig.skywalkingBrowserVersion?.trim() || '0.1.0',
  };
}

export function initializeBrowserMonitoring(router: RouterLike): (() => void) | undefined {
  const config = getBrowserMonitoringConfig();
  if (!config.enabled) {
    return undefined;
  }

  let lastPagePath = currentPagePath(window.location);

  ClientMonitor.register({
    ...(config.collector ? { collector: config.collector } : {}),
    apiErrors: true,
    detailMode: true,
    jsErrors: true,
    pagePath: lastPagePath,
    resourceErrors: true,
    service: config.service,
    serviceVersion: config.serviceVersion,
    useWebVitals: true,
  });

  return router.subscribe((state) => {
    if (state.navigation.state !== 'idle') {
      return;
    }

    const nextPagePath = currentPagePath(state.location);
    if (nextPagePath === lastPagePath) {
      return;
    }

    lastPagePath = nextPagePath;
    ClientMonitor.setPerformance({
      ...(config.collector ? { collector: config.collector } : {}),
      pagePath: nextPagePath,
      service: config.service,
      serviceVersion: config.serviceVersion,
    });
  });
}
